import { Button, Typography } from "antd";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { cglFormatDate } from "utils/functions";
import { getData } from "../../../../apiServices/common";
import AppContent from "../../../../common/AppContent";
import HandleNotification from "../../../../common/Notification";
import AppPageHeader from "../../../../common/PageHeader";
import ExtraContent from "../../../Configuration/CommonComponents/ExtraContent";
import FoldingToPackingTable from "./FoldingToPackingTable";

const FoldingToPacking = (props) => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState();
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterValues, setFilterValues] = useState({});
  

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const onFilterChange = (values) => {
    setFilterValues(values);
  };

  const fetchData = useCallback(
    async (filterValues) => {
      const query = `/api/digitalization/garment-received-from-finishing-list`;
      const bodyData = {
        page: page,
        per_page: filterValues?.per_page || 10,
        ...(filterValues?.line_id && { line_id: filterValues?.line_id }),
        ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
        ...(filterValues?.po_map_id && { po_map_id: filterValues?.po_map_id }),
      };

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        setTableData(response?.data?.data?.data);
        setPageSize(response?.data?.data?.per_page);
        setTotal(response?.data?.data?.total);
      } else {
        HandleNotification("error", "Error", "Something went wrong");
      }
    },
    [page],
  );

  useEffect(() => {
    const value = {
      ...filterValues,
      page: page,
    };
    fetchData(value);
  }, [refresh, page, pageSize, filterValues]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEdit = (event, record) => {
    navigate(`/digitalization/packing/folding-to-packing/edit/${record.id}`);
  };


  const tableColumns = [
    {
      title: "Entry Date",
      dataIndex: "entry_date",
      key: "entry_date",
      width: "120px",
      render: (row) => cglFormatDate(row),
    },
    {
      title: "Time Range",
      key: "time_range",
      width: "150px",
      render: (record) => `${record.hour_time_range_from} - ${record.hour_time_range_to}`,
    },
    {
      title: "Style No",
      dataIndex: ["style_info", "style_no"],
      key: "style_no",
      width: "100px",
    },
    {
      title: "Sub Style No",
      dataIndex: ["sub_style_info", "sub_style_no"],
      key: "sub_style_no",
      width: "120px",
    },
    {
      title: "PO Number",
      dataIndex: ["po_map_info", "original_po"],
      key: "po_number",
      width: "120px",
    },
    {
      title: "Contract Ref",
      dataIndex: ["sales_contract_info", "reference_no"],
      key: "contract_ref",
      width: "150px",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer_info", "name"],
      key: "buyer",
      width: "150px",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office_info", "name"],
      key: "buying_office",
      width: "150px",
    },
    {
      title: "Color",
      dataIndex: ["color_info", "name"],
      key: "color",
      width: "120px",
    },
    {
      title: "Size",
      dataIndex: ["size_info", "name"],
      key: "size",
      width: "80px",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: "100px",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "100px",
      render: (status) => status.charAt(0).toUpperCase() + status.slice(1),
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      render: (_, record) => (
        <div>
          <Button
            style={{ marginLeft: 5 }}
            size="small"
            onClick={(event) => {
              handleEdit(event, record);
            }}
            // disabled={!hasPermission([USER_EDIT_PERMISSION])}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];


  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={onFilterChange}
        filterName={"folding-to-packing"}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
      />
    ),
    [showFilter],
  );

  const header = (
    <AppPageHeader
      extra={
        <ExtraContent
          refresh={refresh}
          setRefresh={setRefresh}
          handleAddItemOpen={fetchData}
          hideAddButton={true}
          setShowFilter={setShowFilter}
          showFilter={showFilter}
        />
      }
      title={<Title level={5}>Packing (Folding to Packing)</Title>}
    />
  );
  const content = (
    <>
      {memoizedCustomFilter}
      <FoldingToPackingTable
        onChange={(newPage) => {
          onPageChange(newPage);
        }}
        total={total}
        current={page}
        defaultCurrent={page}
        pageSize={pageSize}
        dataSource={tableData}
        tableColumns={tableColumns}
        pagination={true}
      />
    </>
  );
  return (
    <>
      <AppContent header={header} content={content} />
    </>
  );
};

export default FoldingToPacking;
